import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import {
  ArrowBackIos as ArrowLeftIcon,
  ArrowForwardIos as ArrowRightIcon,
  CalendarToday as CalendarIcon,
  NewReleases as NewIcon,
  TrendingUp as TrendingIcon,
  Euro as EuroIcon,
  Campaign as MegaphoneIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { axiosService } from 'shared/helpers/axios';

// Import category images
import agricultureImg from '../../../media/categories/agriculture.jpg';
import healthImg from '../../../media/categories/health.jpg';
import climateImg from '../../../media/categories/climate.jpg';
import { formatCompactMoney } from 'shared/helpers/money';

const CARD_WIDTH = 360;
const CARD_HEIGHT = 280;
const CARD_GAP = 20;
const SCROLL_AMOUNT = CARD_WIDTH + CARD_GAP;

// Category image mapping
const CATEGORY_IMAGES = {
  agriculture: agricultureImg,
  farming: agricultureImg,
  food: agricultureImg,
  rural: agricultureImg,
  health: healthImg,
  healthcare: healthImg,
  medical: healthImg,
  medicine: healthImg,
  clinical: healthImg,
  biomedical: healthImg,
  climate: climateImg,
  // environment: climateImg,
  energy: climateImg,
  sustainability: climateImg,
};

const CarouselTrack = styled(Box)({
  display: 'flex',
  gap: `${CARD_GAP}px`,
  overflowX: 'auto',
  scrollBehavior: 'smooth',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
  padding: '8px 4px',
});

const NewsCard = styled(Card)(({ theme }) => ({
  minWidth: CARD_WIDTH,
  maxWidth: CARD_WIDTH,
  height: CARD_HEIGHT,
  flexShrink: 0,
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  position: 'relative',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: `0 20px 40px ${alpha(theme.palette.common.black, 0.25)}`,
    '& .overlay': {
      background: `linear-gradient(to top, ${alpha(theme.palette.common.black, 0.85)} 0%, ${alpha(theme.palette.common.black, 0.3)} 60%, transparent 100%)`,
    },
  },
}));

const NavButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 2,
  backgroundColor: alpha(theme.palette.background.paper, 0.95),
  backdropFilter: 'blur(8px)',
  boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.15)}`,
  '&:hover': {
    backgroundColor: theme.palette.background.paper,
    boxShadow: `0 6px 16px ${alpha(theme.palette.common.black, 0.2)}`,
  },
  width: 40,
  height: 40,
}));

function formatDeadline(raw) {
  if (!raw) return null;
  const d = new Date(raw);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}


function statusColor(status) {
  switch (status) {
    case 'open': return 'success';
    case 'upcoming': return 'info';
    case 'closed': return 'error';
    default: return 'default';
  }
}

// Match call categories/skills with available images
function getCallImage(call) {
  // Check call_categories first
  if (call.categories && Array.isArray(call.categories)) {
    for (const cat of call.categories) {
      const categoryName = (typeof cat === 'string' ? cat : cat?.name || '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
      if (CATEGORY_IMAGES[categoryName]) {
        return CATEGORY_IMAGES[categoryName];
      }
    }
  }

  
  // Check call_skills
  if (call.skills && Array.isArray(call.skills)) {
    for (const skill of call.skills) {
      // console.log('Checking skill for image:', skill);
      const skillName = (typeof skill === 'string' ? skill : skill?.name || '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
      if (CATEGORY_IMAGES[skillName]) {
        return CATEGORY_IMAGES[skillName];
      }
    }
  }

  // Check title/description for keywords
  const text = `${call.title || ''} ${call.description || ''}`.toLowerCase();
  for (const [key, img] of Object.entries(CATEGORY_IMAGES)) {
    if (text.includes(key)) {
      return img;
    }
  }

  return null;
}

const NEW_DAYS = 30;          // the latest calls: added in the last 30 days (CallsList's latest list)
const NEW_POOL = 20;          // the "New calls" card shows 5 of the newest this many, picked at random (small: it loads fast)

// Fisher-Yates shuffle so the order is randomised on every load
function shuffle(rows) {
  const out = [...rows];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

// small filled chips in soft colours: the deadline in the primary tint, the budget in a soft green
const metaChip = { height: 16, fontSize: 9.5, fontWeight: 500, flexShrink: 0, border: 0,
  '& .MuiChip-icon': { fontSize: 10.5, ml: 0.5, mr: -0.25, color: 'inherit' }, '& .MuiChip-label': { px: 0.6 } };
const deadlineChip = { ...metaChip, bgcolor: (t) => alpha(t.palette.primary.main, 0.05), color: 'primary.dark' };
const budgetChip = { ...metaChip, bgcolor: (t) => alpha(t.palette.success.main, 0.06), color: 'success.dark' };

/** The first card: five of the calls added recently, picked at random on every visit (from those relevant to you
 *  when your profile matches any),
 *  with their deadline and budget, how many are new for you since your last visit (the sidebar's number),
 *  and a way to the whole list of latest calls. */
// the agency's acronym, or the initials of the main words of its name (at most three letters)
const SMALL_WORDS = new Set(['of', 'and', 'for', 'the', 'de', 'la', 'du', 'des', 'und', 'für', 've', '&']);
function agencyInitials(call) {
  const acronym = (call.agency_acronym || '').trim();
  if (acronym && acronym.length <= 5) return acronym;
  const words = (call.agency_name ?? call.agency?.name ?? '').split(/[\s-]+/).filter((w) => w && !SMALL_WORDS.has(w.toLowerCase()));
  return words.slice(0, 3).map((w) => w[0].toUpperCase()).join('') || '?';
}

function NewCallsCard({ latest, total, relevant, newForYou, onShowLatest }) {
  return (
    <NewsCard elevation={3} onClick={() => onShowLatest(relevant)} role="button" aria-label="Show the latest calls"
      sx={{ bgcolor: 'background.paper', color: 'text.primary', border: 1, borderColor: 'divider' }}>
      <CardActionArea sx={{ height: '100%' }}>
        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2, pb: '14px !important' }}>
          <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.75 }}>
            <MegaphoneIcon sx={{ color: 'primary.main', fontSize: 26, flexShrink: 0 }} />
            <Box flex={1} minWidth={0}>
              <Typography sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1.2, color: 'primary.main' }}>
                {relevant ? 'New calls for you' : 'New calls'}
              </Typography>
              {newForYou > 0 && (
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{newForYou} new since your last visit</Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            {latest.slice(0, 5).map((c) => {
              const deadline = formatDeadline(c.closes_at ?? c.deadline);
              const budget = formatCompactMoney(c.total_budget ?? c.budget_max, c.currency);
              return (
                <Box key={c.id} display="flex" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                  {/* the agency as an avatar; the programme under the call title */}
                  <Tooltip title={c.agency_name ?? c.agency?.name ?? ''}>
                    <Avatar sx={{ width: 26, height: 26, flexShrink: 0, fontSize: agencyInitials(c).length > 3 ? 8 : 10, fontWeight: 700,
                      bgcolor: (t) => alpha(t.palette.primary.main, 0.12), color: 'primary.dark' }}>
                      {agencyInitials(c)}
                    </Avatar>
                  </Tooltip>
                  <Box flex={1} minWidth={0}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {c.title}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 10.5, color: 'text.secondary', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {c.program_name ?? c.program?.name ?? ''}
                    </Typography>
                    {deadline && <Chip size="small" icon={<CalendarIcon />} label={deadline} sx={deadlineChip} />}
                    {budget && <Chip size="small" icon={<EuroIcon />} label={budget} sx={budgetChip} />}
                  </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, mt: 0.25, color: 'primary.main' }}>
            {total > 5 ? `Click for all ${total} latest calls →` : 'Click for the latest calls →'}
          </Typography>
        </CardContent>
      </CardActionArea>
    </NewsCard>
  );
}

// what the last visit loaded (this session): shown at once on the next, while fresh data comes
const cache = { latest: null, calls: null };

// five random calls among those added in the last NEW_DAYS days for the "New calls" card: those relevant to the
// person when there are any, otherwise all of them. Both asked at once; yours shown as soon as they come.
let latestLoading = null;
function loadLatest() {
  if (latestLoading) return latestLoading;
  const url = (relevant) => `/funding/calls/?statuses=open,upcoming&added_within_days=${NEW_DAYS}&ordering=-created&page_size=${NEW_POOL}${relevant ? '&relevant=1' : ''}`;
  const rows = (res) => {
    const all = res?.data?.results || (Array.isArray(res?.data) ? res.data : []);
    return { calls: shuffle(all).slice(0, 5), total: res?.data?.count ?? all.length };
  };
  const none = () => ({ calls: [], total: 0 });
  const everyone = axiosService.get(url(false)).then(rows).catch(none);
  latestLoading = axiosService.get(url(true)).then(rows).catch(none)
    .then(async (m) => (m.calls.length ? { ...m, relevant: true, ready: true } : { ...(await everyone), relevant: false, ready: true }))
    .then((next) => { cache.latest = next; return next; })
    .finally(() => { latestLoading = null; });
  return latestLoading;
}

/** Fetched in the background a few seconds after the app opens (GlobalLayout asks), so the funding page shows the
 *  card at once. Once a session; the timer is the module's, so re-rendering the layout does not cancel it. */
let preloadTimer = null;
export function preloadLatestCalls(delay = 3000) {
  if (preloadTimer || cache.latest) return;
  preloadTimer = setTimeout(() => { if (!cache.latest) loadLatest(); }, delay);
}

export default function LatestCallsCarousel({ onShowLatest, newForYou = 0 }) {
  const navigate = useNavigate();
  // READY: the "New calls" card has its calls (until then its place, the first, holds a placeholder)
  const [latest, setLatest] = useState(cache.latest || { calls: [], total: 0, relevant: false, ready: false });

  // the card's calls: those preloaded (shown already), then a fresh pick
  useEffect(() => {
    if (!onShowLatest) return undefined;
    let live = true;
    loadLatest().then((next) => { if (live) setLatest(next); });
    return () => { live = false; };
  }, [Boolean(onShowLatest)]);   // eslint-disable-line react-hooks/exhaustive-deps
  const trackRef = useRef(null);
  const [calls, setCalls] = useState(cache.calls || []);
  const [loading, setLoading] = useState(!cache.calls);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  useEffect(() => {
    axiosService
      .get('/funding/calls/?statuses=open,upcoming&page_size=30')
      .then((res) => {
        const results = Array.isArray(res.data?.results)
          ? res.data.results
          : Array.isArray(res.data)
          ? res.data
          : [];
        cache.calls = shuffle(results);
        setCalls(cache.calls);
      })
      .catch(() => setCalls([]))
      .finally(() => setLoading(false));
  }, []);

  const updateArrows = () => {
    const el = trackRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener('scroll', updateArrows);
    return () => el.removeEventListener('scroll', updateArrows);
  }, [calls, latest]);

  const scroll = (dir) => {
    const el = trackRef.current;
    if (el) el.scrollBy({ left: dir * SCROLL_AMOUNT * 2, behavior: 'smooth' });
  };

  if (!loading && calls.length === 0) return null;

  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <TrendingIcon color="primary" fontSize="medium" />
        <Typography variant="h5" fontWeight={700} color="text.primary">
          Latest Funding Opportunities
        </Typography>
      </Box>

      <Box sx={{ position: 'relative' }}>
        {canLeft && (
          <NavButton sx={{ left: -20 }} onClick={() => scroll(-1)} size="small" aria-label="Scroll left">
            <ArrowLeftIcon fontSize="small" />
          </NavButton>
        )}

        <CarouselTrack ref={trackRef}>
          {loading
            ? Array.from({ length: 4 }).map((_, i) => (
                <Box key={i} sx={{ minWidth: CARD_WIDTH, height: CARD_HEIGHT, flexShrink: 0 }}>
                  <Skeleton variant="rounded" width={CARD_WIDTH} height={CARD_HEIGHT} sx={{ borderRadius: 2 }} />
                </Box>
              ))
            : [
              // the "New calls" card first, from the start: a placeholder until its calls are in
              ...(onShowLatest && !latest.ready
                ? [<Box key="new-calls" sx={{ minWidth: CARD_WIDTH, height: CARD_HEIGHT, flexShrink: 0 }}>
                    <Skeleton variant="rounded" width={CARD_WIDTH} height={CARD_HEIGHT} sx={{ borderRadius: 2 }} />
                  </Box>]
                : []),
              ...(onShowLatest && latest.ready && latest.calls.length > 0
                ? [<NewCallsCard key="new-calls" latest={latest.calls} total={latest.total} relevant={latest.relevant}
                    newForYou={newForYou} onShowLatest={onShowLatest} />]
                : []),
              ...calls.map((call) => {
                const deadline = formatDeadline(call.deadline ?? call.closes_at ?? call.closing_date);
                const status = call.status || call.state || 'open';
                const agencyName = call.agency_name ?? call.agency?.name ?? '';
                const programName = call.program_name ?? call.program?.name ?? '';
                const budget = formatCompactMoney(call.total_budget ?? call.budget_max, call.currency);
                const bgImage = getCallImage(call);

                return (
                  <NewsCard 
                    key={call.id} 
                    elevation={3}
                    onClick={() => navigate(`/funding/call/${call.id}`)}
                  >
                    {/* Background */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundImage: bgImage
                          ? `url(${bgImage})`
                          : `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        filter: 'brightness(0.9)',
                      }}
                    />
                    {/* Default decoration — megaphone at top-right when no category image */}
                    {!bgImage && (
                      <MegaphoneIcon sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        fontSize: 92,
                        color: '#ffffff',
                        opacity: 0.15,
                        pointerEvents: 'none',
                      }} />
                    )}

                    {/* Gradient Overlay */}
                    <Box
                      className="overlay"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: `linear-gradient(to top, ${alpha('#000', 0.8)} 0%, ${alpha('#000', 0.4)} 50%, transparent 100%)`,
                        transition: 'background 0.3s ease',
                      }}
                    />

                    {/* Content */}
                    <CardActionArea sx={{ height: '100%', position: 'relative', zIndex: 1 }}>
                      <CardContent 
                        sx={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          height: '100%',
                          justifyContent: 'space-between',
                          p: 3,
                        }}
                      >
                        {/* Top Section - Status & Agency */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                          <Chip
                            label={status.charAt(0).toUpperCase() + status.slice(1)}
                            // color={statusColor(status)}
                            // backgroundColor={statusColor(status)}
                            size="small"
                            sx={{ 
                              fontWeight: 700, 
                              fontSize: 11,
                              backdropFilter: 'blur(8px)',
                              backgroundColor: alpha('#fff', 0.95),
                            }}
                          />
                          {budget && (
                            <Chip
                              icon={<EuroIcon sx={{ fontSize: 14 }} />}
                              label={budget}
                              size="small"
                              sx={{ 
                                fontWeight: 700,
                                fontSize: 11,
                                backdropFilter: 'blur(8px)',
                                backgroundColor: alpha('#fff', 0.95),
                                // color: 'success.dark',
                              }}
                            />
                          )}
                        </Box>

                        {/* Middle Section - Spacer */}
                        <Box sx={{ flex: 1 }} />

                        {programName && (
                          <Box sx={{ mb: 2 }}>
              
                            <Typography
                              variant="h3"
                              fontWeight={800}
                              color="white"
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                lineHeight: 1.3,
                                textShadow: '0 2px 8px rgba(0,0,0,0.5)',
                              }}
                            >
                              {programName}
                            </Typography>
                          </Box>
                        )}


                        {/* Bottom Section - Title & Details */}
                        <Box>
                          <Typography
                            variant="h6"
                            fontWeight={800}
                            color="white"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              lineHeight: 1.3,
                              mb: 1.5,
                              textShadow: '0 2px 8px rgba(0,0,0,0.5)',
                            }}
                          >
                            {call.title}
                          </Typography>

                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {agencyName && (
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: 'white',
                                  fontWeight: 600,
                                  textShadow: '0 1px 4px rgba(0,0,0,0.5)',
                                  mb:2
                                }}
                              >
                                {agencyName}
                              </Typography>
                            )}

                            {deadline && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CalendarIcon sx={{ fontSize: 14, color: 'white' }} />
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: 'white',
                                    fontWeight: 600,
                                    textShadow: '0 1px 4px rgba(0,0,0,0.5)',
                                  }}
                                >
                                  Deadline: {deadline}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </NewsCard>
                );
              }),
            ]}
        </CarouselTrack>

        {canRight && (
          <NavButton sx={{ right: -20 }} onClick={() => scroll(1)} size="small" aria-label="Scroll right">
            <ArrowRightIcon fontSize="small" />
          </NavButton>
        )}
      </Box>
    </Box>
  );
}
